import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Music, Disc, Star, ArrowRight, Play, Pause, ShoppingCart } from 'lucide-react';
import { album } from '../mock';
import { useNavigate } from 'react-router-dom';
import { audioManager } from '../lib/audioManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRACKLIST = [
  { num: 1, title: "Pure Love", feat: "feat. Starr Starks" },
  { num: 2, title: "Summertime" },
  { num: 3, title: "The Setup" },
  { num: 4, title: "When I'm With You", note: "Special Edition" },
  { num: 5, title: "Easier" },
  { num: 6, title: "Time Away" },
  { num: 7, title: "Beautiful" },
  { num: 8, title: "All The Wrong Places" },
  { num: 9, title: "Real Love", note: "Original Mix" },
  { num: 10, title: "Cold" },
  { num: 11, title: "Never Have To Leave" },
  { num: 12, title: "So Long, So Wrong" },
  { num: 13, title: "She Loves Me Not" },
  { num: 14, title: "Real Love" },
  { num: 15, title: "Real Smooth" },
  { num: 16, title: "Untitled 2", note: "I Know What 2 Do" },
  { num: 17, title: "I Don't Wanna Live" },
  { num: 18, title: "It Grows" },
  { num: 19, title: "Rosalie" },
  { num: 20, title: "Promise To Love You" },
  { num: 21, title: "Moments of Passion", note: "Instrumental" },
];

const TrackRow = ({ track }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [buying, setBuying] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const stopSelf = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    if (timerRef.current) clearTimeout(timerRef.current);
    setIsPlaying(false);
    audioManager.clearNowPlaying(stopSelfRef.current);
  }, []);

  const stopSelfRef = useRef(stopSelf);
  stopSelfRef.current = stopSelf;

  useEffect(() => {
    const fn = stopSelfRef.current;
    audioManager.register(fn);
    return () => audioManager.unregister(fn);
  }, []);

  const handlePlay = async () => {
    if (isPlaying) {
      stopSelf();
      return;
    }

    if (!audioRef.current) {
      setIsLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/store/preview/${track.num}`);
        if (!res.ok) { setIsLoading(false); return; }
        const data = await res.json();
        audioRef.current = new Audio(data.url);
        audioRef.current.addEventListener('ended', () => {
          setIsPlaying(false);
          audioManager.clearNowPlaying(stopSelfRef.current);
        });
      } catch (e) { setIsLoading(false); return; }
    }

    try {
      audioRef.current.currentTime = 0;
      // Tell the global manager to crossfade and show Now Playing
      await audioManager.requestPlayback(stopSelfRef.current, {
        title: track.title,
        subtitle: track.feat || 'The Gordon Principle',
        audio: audioRef.current,
      });
      await audioRef.current.play();
      setIsPlaying(true);
      setIsLoading(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        stopSelf();
      }, 30000);
    } catch (e) { setIsLoading(false); }
  };

  const handleBuy = async () => {
    setBuying(true);
    try {
      const productId = `track-${String(track.num).padStart(2, '0')}`;
      const res = await fetch(`${API_URL}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, origin_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      }
    } catch (e) { console.error('Checkout error:', e); }
    setBuying(false);
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div
      className="flex items-center gap-3 py-2 px-3 rounded-lg transition-colors group hover:bg-teal-900/20"
      data-testid={`tracklist-row-${track.num}`}
    >
      <button
        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all flex-shrink-0 ${isPlaying ? 'bg-gold-500 text-black' : 'bg-teal-700/60 text-teal-100 hover:bg-teal-600'} hover:scale-105`}
        aria-label={isPlaying ? "Pause preview" : "Play preview"}
        data-testid={`tracklist-play-${track.num}`}
        onClick={handlePlay}
        disabled={isLoading}
      >
        {isLoading ? <div className="w-3 h-3 border-2 border-teal-200 border-t-transparent rounded-full animate-spin" /> : isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" className="ml-0.5" />}
      </button>
      <span className="text-teal-500 text-sm font-mono w-6 text-right flex-shrink-0">
        {track.num.toString().padStart(2, '0')}
      </span>
      <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
        <span className="text-teal-50 font-medium truncate">{track.title}</span>
        {track.feat && <span className="text-teal-400 text-sm">{track.feat}</span>}
        {track.note && <span className="text-teal-500/70 text-xs italic">({track.note})</span>}
      </div>
      <button
        className="bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black text-xs font-extrabold px-4 py-2 rounded-full flex items-center gap-1.5 transition-all hover:scale-105 flex-shrink-0 shadow-md shadow-gold-500/30"
        aria-label={`Buy ${track.title} for $0.99`}
        data-testid={`tracklist-buy-${track.num}`}
        onClick={handleBuy}
        disabled={buying}
      >
        <ShoppingCart size={13} />
        {buying ? '...' : '$0.99'}
      </button>
    </div>
  );
};

export const AlbumSection = () => {
  const navigate = useNavigate();

  return (
    <section id="album" className="py-24 bg-gradient-to-br from-teal-950 via-gray-900 to-black relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gold-500/20 backdrop-blur-sm border border-gold-500/30 rounded-full px-6 py-2 mb-6">
              <Star size={14} className="text-gold-400 fill-gold-400" />
              <span className="text-gold-400 font-semibold uppercase tracking-wider text-sm">Out Now</span>
              <Star size={14} className="text-gold-400 fill-gold-400" />
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-teal-50 mb-4">
              The Gordon Principle
            </h1>
            <p className="text-xl md:text-2xl text-teal-200">
              21 tracks of soulful originals — available now
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Album Cover */}
            <div className="flex justify-center lg:justify-end order-2 lg:order-1">
              <div className="relative group max-w-lg w-full sticky top-32">
                <div className="absolute -inset-6 bg-gradient-to-r from-teal-500 via-gold-500 to-teal-500 rounded-3xl blur-3xl opacity-30 group-hover:opacity-50 transition duration-700 pointer-events-none"></div>
                <img 
                  src={album.coverArt} 
                  alt="The Gordon Principle Album Cover" 
                  className="relative w-full rounded-2xl shadow-2xl transform group-hover:scale-105 transition duration-500"
                />
                {/* Buy CTA under cover */}
                <div className="mt-6 flex flex-col items-center gap-3">
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/store/checkout`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ product_id: 'album-the-gordon-principle', origin_url: window.location.origin })
                        });
                        if (res.ok) {
                          const data = await res.json();
                          window.location.href = data.url;
                        }
                      } catch (e) { console.error('Checkout error:', e); }
                    }}
                    className="w-full bg-gold-500 hover:bg-gold-600 text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-200 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                    data-testid="album-buy-btn"
                  >
                    <Disc size={20} />
                    Buy Album — $7.99
                  </button>
                </div>

                {/* Quote */}
                <div className="hidden lg:block mt-8 bg-gradient-to-br from-teal-900/40 via-black/40 to-gold-900/20 border border-gold-500/30 rounded-2xl p-8 shadow-xl relative">
                  <div className="absolute top-3 left-5 text-6xl text-gold-500/40 leading-none font-serif">&ldquo;</div>
                  <p className="relative text-teal-50 text-lg md:text-xl italic leading-relaxed pl-6">A lifetime of music, distilled into every note.</p>
                  <p className="relative text-gold-400/80 text-xs uppercase tracking-[0.3em] font-semibold mt-4 pl-6">— The Gordon Principle</p>
                </div>

                {/* Studio portrait */}
                <div className="hidden lg:block mt-6 relative rounded-2xl overflow-hidden group ring-1 ring-gold-500/20 hover:ring-gold-500/50 transition-all duration-500">
                  <img
                    src="https://customer-assets.emergentagent.com/job_d063d7a9-eda0-44cf-9fcf-715c92eeba27/artifacts/36c2ki6z_20260611_211833-IMG_STYLE.jpg"
                    alt="Brooksgordon at the studio grand piano"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <p className="text-gold-400 text-[0.7rem] uppercase tracking-[0.25em] font-semibold mb-1">Where It Was Made</p>
                    <p className="text-teal-50 text-lg font-bold">The Brooksgordon Studio</p>
                  </div>
                </div>

                {/* By The Numbers */}
                <div className="hidden lg:block mt-6 rounded-2xl bg-black/50 backdrop-blur-sm border border-teal-900/50 p-6 shadow-xl">
                  <p className="text-gold-400 text-[0.7rem] uppercase tracking-[0.3em] font-semibold mb-4">By The Numbers</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-3xl font-bold text-teal-50">21</p>
                      <p className="text-teal-400 text-xs uppercase tracking-wider mt-1">Tracks</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-teal-50">30+</p>
                      <p className="text-teal-400 text-xs uppercase tracking-wider mt-1">Years Trained</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-teal-50">$7.99</p>
                      <p className="text-teal-400 text-xs uppercase tracking-wider mt-1">Full Album</p>
                    </div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-teal-900/50 flex flex-wrap justify-center gap-2">
                    {['Soul', 'R&B', 'Jazz', 'Piano', 'Vocal', 'Original'].map((genre) => (
                      <span key={genre} className="text-[0.65rem] uppercase tracking-widest text-teal-300 bg-teal-900/40 border border-teal-700/40 rounded-full px-3 py-1 font-semibold">{genre}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Album Info + Tracklist */}
            <div className="space-y-8 order-1 lg:order-2">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gold-400">About The Album</h2>
                <p className="text-lg text-teal-100 leading-relaxed">
                  {album.description}
                </p>
                <p className="text-teal-200 leading-relaxed">
                  This album represents the culmination of over 30 years of musical mastery, 
                  blending classical piano training with contemporary R&amp;B soul. Each track is a testament to 
                  the artistry and dedication that defines Brooksgordon.
                </p>
                <h3 className="text-xl font-bold text-gold-400 pt-2">A Note on AI</h3>
                <p className="text-teal-200 leading-relaxed">
                  A bit of AI was used very sparsely to finally release songs that were collecting dust because 
                  certain musicians refused to lend their talents to the project. Some of the music is outside of the 
                  genre I can actually perform myself convincingly, so I used AI to give those compositions their proper 
                  voice. I have performed all of them live before, but I still think that the voice I chose via AI is 
                  better suited to the material.
                </p>
                <p className="text-teal-200 leading-relaxed">
                  I did not allow AI to lyricize or change the melody or cadence of the songs. AI did not contribute 
                  to every song, only those that I needed session musicians and vocalists to save time and money.
                </p>
                <blockquote className="text-teal-200 leading-relaxed italic border-l-2 border-gold-500/50 pl-4">
                  For those interested in performing my song &ldquo;Summertime,&rdquo; I have notated the entire song 
                  with lyrics and piano — <a href="https://www.musicnotes.com/marketplace/sheetmusic/MK0143529" target="_blank" rel="noopener noreferrer" className="text-gold-400 underline hover:text-gold-300">now available on Musicnotes.</a>
                </blockquote>
                <a
                  href="https://www.musicnotes.com/marketplace/sheetmusic/MK0143529"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="summertime-musicnotes-link"
                  className="block bg-black/40 border border-teal-900/30 rounded-xl p-4 hover:border-gold-500/50 transition-all duration-300 group"
                >
                  <span className="text-gold-400 uppercase tracking-widest text-xs font-semibold">Sheet Music Available</span>
                  <div className="flex items-center justify-between mt-1">
                    <div>
                      <p className="text-teal-50 font-medium">&ldquo;Summertime&rdquo; — Piano &amp; Vocal on Musicnotes</p>
                      <p className="text-teal-400 text-sm">Notated with lyrics · digital download</p>
                    </div>
                    <ArrowRight size={18} className="text-teal-400 group-hover:text-gold-400 transition-colors" />
                  </div>
                </a>
              </div>

              {/* Tracklist */}
              <div id="tracklist" className="bg-black/40 backdrop-blur-sm border border-teal-900/30 rounded-2xl p-6 scroll-mt-28">
                <h3 className="text-xl font-bold text-gold-400 mb-2 flex items-center gap-2">
                  <Disc size={20} />
                  Tracklist
                </h3>
                <p className="text-teal-400 text-sm mb-4">Click any track to preview — or buy individual tracks for $0.99</p>
                <div className="space-y-1">
                  {TRACKLIST.map((track) => (
                    <TrackRow key={track.num} track={track} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
